import { EventEmitterInterface } from '../event/emitter.interface';
import { DataKeyValueStringInterface } from '../data/key-value/string.interface';
import { BrowserDocumentServiceInterface } from '../../service/browser-document/service.interface';
import { ModalViewEvent } from './view.event';
import { DomElementEvent } from '../dom/element.event';
import { ViewBasicInterface } from '../view/basic.interface';
import { ModalServiceInterface } from '../../service/modal/service.interface';
import { ViewOptionsInterface } from '../view/options.interface';
import { WindowServiceInterface } from '../../service/window/service.interface';

export class ModalView implements ViewBasicInterface, ModalServiceInterface {
  /**
   * HTML element containing the modal view
   */
  protected element: HTMLElement;

  /**
   * HTML element in which the content view should be displayed
   */
  protected contentElement: HTMLElement;

  /**
   * Overlay element
   */
  protected overlayElement: HTMLElement;

  /**
   * View displayed inside the modal
   */
  protected contentView: ViewBasicInterface | null = null;

  /**
   * Cached scroll position
   */
  protected scrollY: number = null;

  /**
   * CSS selectors
   */
  protected cssSelector: DataKeyValueStringInterface = {
    content: '[data-qs="modal_content"]',
    overlay: '[data-qs="modal_overlay"]',
  };

  /**
   * CSS classes
   */
  protected cssClass: DataKeyValueStringInterface = {
    modalOpen: 'modal-open',
    overlayOpen: 'modal_overlay-open',
  };

  /**
   *
   * @param eventEmitter
   * @param browserDocumentService
   * @param windowService
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected browserDocumentService: BrowserDocumentServiceInterface,
    protected windowService: WindowServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: ViewOptionsInterface): void {
    this.element = options.element;

    this.contentElement = <HTMLElement>this.element.querySelector(this.cssSelector.content);
    this.overlayElement = <HTMLElement>this.element.querySelector(this.cssSelector.overlay);
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    this.overlayElement.addEventListener(DomElementEvent.click, this.onClickOverlay);
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritDoc
   */
  public open(): void {
    this.element.classList.add(this.cssClass.modalOpen);
    this.overlayElement.classList.add(this.cssClass.overlayOpen);

    // Prevents document scroll

    this.scrollY = this.windowService.getScrollTop();

    this.browserDocumentService.setScrollable(false);

    this.eventEmitter.emit(ModalViewEvent.open);
  }

  /**
   * @inheritDoc
   */
  public close(): void {
    this.element.classList.remove(this.cssClass.modalOpen);
    this.overlayElement.classList.remove(this.cssClass.overlayOpen);

    // Allows document scroll
    this.browserDocumentService.setScrollable(true);
    this.windowService.getNative().scrollTo(0, this.scrollY);

    this.eventEmitter.emit(ModalViewEvent.close);
  }

  /**
   * @inheritDoc
   */
  public setContentView(view: ViewBasicInterface | null): void {
    this.contentView = view;
  }

  /**
   * @inheritDoc
   */
  public getContentView(): ViewBasicInterface | null {
    return this.contentView;
  }

  /**
   * @inheritDoc
   */
  public getContentElement(): HTMLElement {
    return this.contentElement;
  }

  /**
   * Disable close modal window by clicking on overlay
   */
  public disableClickOverlay(): void {
    this.overlayElement.removeEventListener(DomElementEvent.click, this.onClickOverlay);
  }

  /**
   * Clicked the overlay
   */
  protected onClickOverlay = (): void => {
    this.close();
  };
}
